import React, { useState, useMemo, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import RoleAPIs from "../../apiRoutes/Role";
import { GET_PERMISSIONS_QUERY_KEY } from "../../utils/constants/queryKeys";
import ButtonLoader from "../Icons/ButtonLoader";
import { Button } from "../ui/button";
import AddPermissionModal from "./Modals/AddPermissionModal";

const getCategory = (code) => {
    const i = (code || "").indexOf(".");
    return i > 0 ? code.slice(0, i) : (code || "");
};

const LIMIT = 20;

const PermissionTab = () => {
    const [modal, setModal] = useState(false);
    const [page, setPage] = useState(0);
    const axiosPrivate = useAxiosPrivate();

    const { data: result, isLoading } = useQuery({
        queryKey: [...GET_PERMISSIONS_QUERY_KEY, page],
        queryFn: () => RoleAPIs.listPermissions({ axiosPrivate, limit: LIMIT, offset: page * LIMIT }),
    });

    const permissions = useMemo(() => result?.data ?? [], [result?.data]);
    const totalCount = result?.count ?? 0;

    const grouped = useMemo(() => {
        if (!permissions.length) return [];
        const map = {};
        permissions.forEach((p) => {
            const cat = getCategory(p.code);
            if (!map[cat]) map[cat] = [];
            map[cat].push(p);
        });
        return Object.keys(map)
            .sort()
            .map((category) => ({ category, items: map[category].sort((a, b) => a.code.localeCompare(b.code)) }));
    }, [permissions]);

    useEffect(() => setPage(0), []);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <ButtonLoader color="#5E30BF" />
                <span className="ml-2 text-primaryPurple">Loading Permissions...</span>
            </div>
        );
    }

    return (
        <div className="p-6">
            <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-semibold text-gray-800">Permissions</h3>
                <button
                    onClick={() => setModal(true)}
                    className="px-4 py-2 bg-primaryPurple text-white rounded-lg text-sm font-medium hover:bg-primaryPurple/90 transition-colors"
                >
                    Add Permission
                </button>
            </div>

            <div className="flex flex-col min-h-[calc(100vh-200px)]">
                <div className="overflow-x-auto flex-1 min-h-0 rounded-xl border border-gray-200 bg-white">
                <table className="w-full text-left border-collapse">
                    <thead>
                        <tr className="border-b border-gray-200">
                            <th className="py-4 px-4 text-sm font-semibold text-gray-600">Code</th>
                            <th className="py-4 px-4 text-sm font-semibold text-gray-600">Description</th>
                        </tr>
                    </thead>
                    <tbody>
                        {grouped.length > 0 ? (
                            grouped.map(({ category, items }, idx) => (
                                <React.Fragment key={category}>
                                    {idx > 0 && (
                                        <tr>
                                            <td colSpan="2" className="py-3" />
                                        </tr>
                                    )}
                                    <tr className="bg-gray-100/80">
                                        <td colSpan="2" className="py-2.5 px-4 text-sm font-semibold text-gray-600 uppercase tracking-wider">
                                            {category}
                                        </td>
                                    </tr>
                                    {items.map((perm) => (
                                        <tr key={perm.id} className="hover:bg-gray-50/50 transition-colors">
                                            <td className="py-4 px-4 text-sm font-medium text-gray-900">{perm.code}</td>
                                            <td className="py-4 px-4 text-sm text-gray-600">{perm.description || "—"}</td>
                                        </tr>
                                    ))}
                                </React.Fragment>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="2" className="py-12 text-center text-gray-400 italic">
                                    No permissions found.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
                </div>
            <div className="flex items-center justify-between px-4 py-2.5 border-t border-border bg-muted/30 flex-shrink-0">
                <span className="text-sm text-muted-foreground">
                    Showing {permissions.length ? page * LIMIT + 1 : 0}–{Math.min(page * LIMIT + permissions.length, totalCount)} of {totalCount}
                </span>
                <div className="flex gap-1.5">
                    <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)} className="h-8">
                        Previous
                    </Button>
                    <Button variant="outline" size="sm" disabled={page * LIMIT + permissions.length >= totalCount} onClick={() => setPage((p) => p + 1)} className="h-8">
                        Next
                    </Button>
                </div>
            </div>
            </div>

            {modal && (
                <AddPermissionModal open={modal} onClose={() => setModal(false)} />
            )}
        </div>
    );
};

export default PermissionTab;
